/**
 * The `gaia_branch` cookie selects which S3 build of the UI the BFF serves, and records why.
 *
 * Encoded as a query string, e.g. `n=ux-1234%2Fnew-equipment&s=labs&v=1`. A value without a `v`
 * key is a legacy bare branch name. This module is the UI's single source of truth for the format;
 * it mirrors `bff/middleware/branch-cookie.go`.
 *
 * @see docs/designs/labs-prototype-assignment.md
 */

export const BRANCH_COOKIE_NAME = 'gaia_branch';

const BRANCH_COOKIE_VERSION = '1';

/**
 * Why a branch cookie is set.
 * - `discretionary`: developer preview via `?branch=` (also legacy and unrecognized values)
 * - `labs`: assigned by the `gaia-prototype` LaunchDarkly flag
 * - `labsOptOut`: the user declined the prototype (no branch)
 */
export type BranchStatus = 'discretionary' | 'labs' | 'labsOptOut';

export interface BranchCookie {
  /** Branch name; empty means main. */
  name: string;
  status: BranchStatus;
}

const SAFE_BRANCH_NAME = /^[A-Za-z0-9._+/-]+$/;

/**
 * Reports whether a branch name is safe to route to. Mirrors `isValidBranchName` in the BFF.
 */
export function isValidBranchName(name: string): boolean {
  if (!SAFE_BRANCH_NAME.test(name)) return false;
  if (name.startsWith('/') || name.endsWith('/') || name.includes('//')) return false;
  if (name.includes('..')) return false;
  return !name.split('/').includes('.');
}

function normalizeStatus(status: string | null): BranchStatus {
  // Unknown statuses fall back to today's developer-preview UX.
  return status === 'labs' || status === 'labsOptOut' ? status : 'discretionary';
}

/**
 * Decodes a raw cookie value. Returns undefined when there is no cookie.
 *
 * A value with an unrecognized `v` is treated as `discretionary` with no branch, so the UI leaves
 * it alone rather than overwriting a cookie written by a newer format.
 */
export function parseBranchCookie(raw: string | undefined): BranchCookie | undefined {
  if (!raw) return undefined;

  const params = new URLSearchParams(raw);
  if (!params.has('v')) {
    // Legacy bare branch name
    return { name: raw, status: 'discretionary' };
  }
  if (params.get('v') !== BRANCH_COOKIE_VERSION) {
    return { name: '', status: 'discretionary' };
  }
  return { name: params.get('n') ?? '', status: normalizeStatus(params.get('s')) };
}

/**
 * Encodes a cookie value. Always use this rather than building the string by hand: `+` decodes as a space.
 */
export function encodeBranchCookie(cookie: BranchCookie): string {
  const params = new URLSearchParams();
  if (cookie.name) params.set('n', cookie.name);
  params.set('s', cookie.status);
  params.set('v', BRANCH_COOKIE_VERSION);
  return params.toString();
}

/**
 * Reads and decodes the branch cookie from `document.cookie`.
 */
export function readBranchCookie(): BranchCookie | undefined {
  const prefix = `${BRANCH_COOKIE_NAME}=`;
  const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  return parseBranchCookie(entry?.slice(prefix.length));
}

/**
 * Writes the branch cookie as a browser-session cookie, like the BFF's `?branch=`.
 * Attributes must match the BFF's exactly (`Path=/`, no `Domain`), or the browser keeps two cookies.
 */
export function writeBranchCookie(cookie: BranchCookie): void {
  document.cookie = `${BRANCH_COOKIE_NAME}=${encodeBranchCookie(cookie)}; Path=/`;
}

/**
 * Deletes the branch cookie, so the BFF serves main.
 */
export function clearBranchCookie(): void {
  document.cookie = `${BRANCH_COOKIE_NAME}=; Path=/; Max-Age=0`;
}
